import { useEffect, useState } from "react";
import { getVideoStream } from "../../../lib/utils";
import { addOrcaEvent } from "../../utils";
import { OrcaWindowEvents, VideoEvents } from "../../../globals";
import PropTypes from "prop-types";

import { dispatchNewEvent } from "../../utils/elementEvents";
import { useVideoDataContext } from "../../contexts/VideoDataContext";
import { useVideoElementRefContext } from "../../contexts/VideoElementRefContext";
import { hasForceHDTag } from "./utils";
import { VideoApiActionTypes } from "../useVideoApi/reducer";

const DefaultHLSConfig = {
  liveDurationInfinity: true,
  capLevelToPlayerSize: true,
  maxBufferLength: 10,
  autoStartLoad: false,
  startLevel: -1
};

export const useHLS = (Hls, config) => {
  const videoElementRef = useVideoElementRefContext();
  const { videoId, videoData, dispatchVideoApiEvent } = useVideoDataContext();

  const { debug, debugHLS, hlsConfig = DefaultHLSConfig } = config;

  const [hls, setHls] = useState(null);

  const removeHLS = () => {
    if (hls) {
      hls.off(Hls.Events.MEDIA_ATTACHED),
        () => {
          addOrcaEvent(
            OrcaWindowEvents.HLS_ATTACHED,
            videoElementRef.src,
            videoId,
            debug
          );
        };
      hls.off(Hls.Events.MANIFEST_PARSED),
        data => {
          addOrcaEvent(
            OrcaWindowEvents.HLS_MANIFEST_PARSED,
            data,
            videoId,
            debug
          );
        };
      hls.detachMedia();
      hls.destroy();
      setHls(null);
      dispatchNewEvent(videoElementRef.current, VideoEvents.DESTROYED);
    }
  };

  if (debugHLS) {
    console.log(hlsConfig);
  }

  const initHlsVideo = videoSource => {
    // See full list of config options: https://github.com/video-dev/hls.js/blob/master/docs/API.md#fine-tuning
    const newHls = new Hls({ ...hlsConfig, debug: debugHLS });

    newHls.on(Hls.Events.MEDIA_ATTACHED, () => {
      addOrcaEvent(OrcaWindowEvents.HLS_ATTACHED, videoSource, videoId, debug);
    });

    newHls.on(Hls.Events.MANIFEST_PARSED, (name, data) => {
      addOrcaEvent(OrcaWindowEvents.HLS_MANIFEST_PARSED, data, videoId, debug);
    });

    newHls.on(Hls.Events.SUBTITLE_TRACK_LOADED, () => {
      dispatchNewEvent(videoElementRef.current, VideoEvents.CAPTIONS_LOADED);
    });

    newHls.loadSource(videoSource);
    newHls.attachMedia(videoElementRef.current);
    newHls.startLoad();
    setHls(newHls);
  };

  /**
   * Sets up the video source based on the given video data.
   * If the video is looping, sets the "NOT_SUPPORTED" attribute for the ad.
   * Determines the appropriate video source based on device type, looping status, and force HD tag.
   * If no video source is found, dispatches a video API event with a failed status.
   * Sets the source of the video element based on the video source type.
   *
   * @returns {void}
   */
  const setUpVideoSource = () => {
    const videoSource = getVideoStream(videoData.streams);
    if (!videoSource) {
      dispatchVideoApiEvent({
        type: VideoApiActionTypes.SET_API_FAILED
      });
      return;
    }

    if (shouldSetupHls(videoSource, Hls, videoElementRef.current)) {
      initHlsVideo(videoSource.url);
    } else {
      videoElementRef.current.src = videoSource.url;
      // don't need to wait on hls captions to load
      dispatchNewEvent(videoElementRef.current, VideoEvents.CAPTIONS_LOADED);
    }
  };

  useEffect(() => {
    // if video is tagged force-hd, start hls on the highest level playlist in the stream
    /// hls may lower quality of the video for low-bandwidth users but hls should start on the highest quality playlist
    const forceHD = hasForceHDTag(videoData);
    if (hls && forceHD) {
      hls.on(Hls.Events.MANIFEST_LOADED, () => {
        hls.startLevel = hls.levels.length - 1;
      });
    }
  }, [hls]);

  return {
    hls,
    removeHLS,
    setUpVideoSource
  };
};

useHLS.propTypes = {
  // is video in carousel
  isCarousel: PropTypes.bool,
  // config for hls
  config: PropTypes.shape({
    debug: PropTypes.bool,
    debugHLS: PropTypes.bool,
    hlsConfig: PropTypes.object
  })
};

export const shouldSetupHls = (videoSource, Hls, hls) => {
  return (
    Hls.isSupported() &&
    videoSource.stream_type !== "mp4" &&
    videoSource.url !== hls?.url
  );
};
